import { useEffect } from "react";
import type { Session } from "@oshimap/auth-core";

/**
 * 서버가 잠깐 안 될 때 잃은 로그인을 되찾습니다.
 *
 * `Session.restore()` 는 확인에 실패하면 **토큰을 지우지 않고** 게스트로
 * 둡니다 — 서버가 죽은 사이에 로그아웃시키면 다시 로그인할 방법도 없기
 * 때문입니다. 코드 주석은 "다음 restore 에서 되살아난다" 고 말합니다.
 *
 * 그런데 `restore()` 를 다시 부르는 곳이 없었습니다. 앱이 뜰 때 한 번뿐이라,
 * 그 순간에 서버가 느렸던 사람은 **새로고침하기 전까지 계속 게스트**입니다.
 * 주석이 말하는 동작이 실제로는 없었습니다.
 *
 * 되찾을 만한 때에만 다시 시도합니다.
 *
 *   · 화면이 다시 보일 때 — 사람이 돌아온 순간
 *   · 연결이 돌아왔을 때 (`online`)
 *
 * 로그아웃했거나(`signed-out`) 토큰이 틀린(`invalid-token`) 경우에는
 * 시도하지 않습니다. 그것은 실패가 아니라 결과입니다.
 */
export function useSessionRecovery(session: Session): void {
  useEffect(() => {
    const retry = () => {
      const state = session.state;
      if (state.status !== "guest" || state.reason !== "unverifiable") return;
      void session.restore();
    };
    const onVisible = () => { if (document.visibilityState === "visible") retry(); };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", retry);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", retry);
    };
  }, [session]);
}
